import React, { useState, useMemo, useEffect } from 'react';
import { 
  UsersIcon, 
  MagnifyingGlassIcon, 
  PlusCircleIcon,
  PencilIcon, 
  TrashIcon,
  KeyIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { TextInput, Button, Select, Badge } from '@mantine/core';
import { modals } from '@mantine/modals';
import { errorToast, successToast } from '../../lib/helpers';
import usersApi from '../../lib/usersApi';
import DataTable from '../AdvancedReports/shared/DataTable';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (branchFilter !== 'all') params.append('branch', branchFilter);

      console.log('[UserManagement] Fetching users from API...');
      const response = await usersApi.get(`/api/users?${params.toString()}`);
      console.log('[UserManagement] API Response:', response);
      
      if (response.success && response.data) {
        // Transform API response to match component expectations
        const transformedUsers = response.data.map(user => ({
          id: user.user_id,
          name: user.username, // Using username as name for now
          email: user.email,
          username: user.username,
          role: user.role,
          status: user.status,
          branch: user.branch,
          lastLogin: user.last_login ? new Date(user.last_login).toLocaleString() : 'Never',
          createdDate: user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
          allowLogin: user.allow_login
        }));
        console.log('[UserManagement] Transformed users:', transformedUsers.length);
        setUsers(transformedUsers);
      } else {
        console.warn('[UserManagement] API response missing data:', response);
        setUsers([]);
      }
    } catch (error) {
      console.error('[UserManagement] Error fetching users:', error);
      console.error('[UserManagement] Error details:', error.message, error.response?.data);
      errorToast(error.message || 'Failed to load users. Make sure the backend server is running on http://localhost:3001');
      // Fallback to empty array on error
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, roleFilter, statusFilter, branchFilter]);

  // Dummy user data (fallback - will be removed once API is working)
  const dummyUsers = [
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@automm.co.uk',
      username: 'john.smith1234',
      role: 'admin',
      status: 'active',
      branch: 'AVDL',
      lastLogin: '2024-01-15 14:30',
      createdDate: '2023-06-01',
      allowLogin: true
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@automm.co.uk',
      username: 'sarah.johnson5678',
      role: 'bm',
      status: 'active',
      branch: 'AVDL2',
      lastLogin: '2024-01-15 09:15',
      createdDate: '2023-07-15',
      allowLogin: true
    },
    {
      id: 3,
      name: 'Mike Williams',
      email: 'mike.williams@automm.co.uk',
      username: 'mike.williams9012',
      role: 'sales',
      status: 'active',
      branch: 'Finchley',
      lastLogin: '2024-01-14 16:45',
      createdDate: '2023-08-20',
      allowLogin: true
    },
    {
      id: 4,
      name: 'Emma Davis',
      email: 'emma.davis@automm.co.uk',
      username: 'emma.davis3456',
      role: 'rep',
      status: 'active',
      branch: 'AVDL',
      lastLogin: '2024-01-15 11:20',
      createdDate: '2023-09-10',
      allowLogin: true
    },
    {
      id: 5,
      name: 'David Brown',
      email: 'david.brown@automm.co.uk',
      username: 'david.brown7890',
      role: 'sales',
      status: 'inactive',
      branch: 'AVDL2',
      lastLogin: '2024-01-10 08:30',
      createdDate: '2023-05-12',
      allowLogin: true
    },
    {
      id: 6,
      name: 'Lisa Anderson',
      email: 'lisa.anderson@automm.co.uk',
      username: 'lisa.anderson2468',
      role: 'bm',
      status: 'active',
      branch: 'Finchley',
      lastLogin: '2024-01-15 13:10',
      createdDate: '2023-10-05',
      allowLogin: true
    },
    {
      id: 7,
      name: 'Tom Wilson',
      email: 'tom.wilson@automm.co.uk',
      username: 'tom.wilson1357',
      role: 'rep',
      status: 'locked',
      branch: 'AVDL',
      lastLogin: '2024-01-08 10:00',
      createdDate: '2023-11-18',
      allowLogin: true
    },
    {
      id: 8,
      name: 'Rachel Taylor',
      email: 'rachel.taylor@automm.co.uk',
      username: 'rachel.taylor8024',
      role: 'admin',
      status: 'active',
      branch: 'AVDL2',
      lastLogin: '2024-01-15 15:45',
      createdDate: '2023-04-22',
      allowLogin: true
    }
  ];

  // Use users from API (filtering is done server-side)
  // But we can also do client-side filtering as fallback
  const filteredUsers = useMemo(() => {
    // If we have users from API, use them (they're already filtered server-side)
    if (users.length > 0 || !loading) {
      return users;
    }
    // Fallback to empty array while loading
    return [];
  }, [users, loading]);

  const handleAddUser = () => {
    modals.openContextModal({
      modal: 'userEditModal',
      title: 'Add New User',
      centered: true,
      size: 'lg',
      innerProps: {
        isEdit: false,
        submit: async (data) => {
          const response = await usersApi.post('/api/users', data);
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message || 'Failed to create user');
          }
        },
        refetch: () => {
          fetchUsers();
        }
      }
    });
  };

  const handleEditUser = (user) => {
    modals.openContextModal({
      modal: 'userEditModal',
      title: 'Edit User',
      centered: true,
      size: 'lg',
      innerProps: {
        isEdit: true,
        defaultValues: {
          username: user.username,
          email: user.email,
          password: '', // Don't pre-fill password
          role: user.role,
          branch: user.branch,
          allow_login: user.allowLogin,
          status: user.status
        },
        submit: async (data) => {
          const response = await usersApi.put(`/api/users/${user.id}`, data);
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message || 'Failed to update user');
          }
        },
        refetch: () => {
          fetchUsers();
        }
      }
    });
  };

  const handleDeleteUser = (user) => {
    modals.openContextModal({
      modal: 'warningModal',
      title: 'Delete User',
      centered: true,
      innerProps: {
        message: `Are you sure you want to delete user "${user.name}" (${user.username})? This action cannot be undone.`,
        submit: async () => {
          try {
            const response = await usersApi.delete(`/api/users/${user.id}`);
            if (response.success) {
              successToast('User deleted successfully');
              fetchUsers();
            } else {
              throw new Error(response.message || 'Failed to delete user');
            }
          } catch (error) {
            errorToast(error.message || 'Failed to delete user');
            throw error;
          }
        }
      }
    });
  };

  const handleResetPassword = (user) => {
    modals.openContextModal({
      modal: 'userEditModal',
      title: 'Reset Password',
      centered: true,
      size: 'md',
      innerProps: {
        isEdit: true,
        defaultValues: {
          username: user.username,
          email: user.email,
          password: '', // Force new password
          role: user.role,
          branch: user.branch,
          allow_login: user.allowLogin,
          status: user.status
        },
        submit: async (data) => {
          if (!data.password) {
            throw new Error('Password is required');
          }
          const response = await usersApi.put(`/api/users/${user.id}/password`, { password: data.password });
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message || 'Failed to reset password');
          }
        },
        refetch: () => {
          fetchUsers();
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'green',
      inactive: 'gray',
      locked: 'red'
    };
    
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      locked: 'Locked'
    };

    return (
      <Badge color={colors[status]} variant="light">
        {labels[status]}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'red',
      bm: 'blue',
      sales: 'purple',
      rep: 'orange',
      fleet: 'teal'
    };
    
    const labels = {
      admin: 'Admin',
      bm: 'Branch Manager',
      sales: 'Sales',
      rep: 'Rep',
      fleet: 'Fleet'
    };

    return (
      <Badge color={colors[role]} variant="light">
        {labels[role]}
      </Badge>
    );
  };

  // Selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(user => user.id));
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selectedUserIds.length === 0) {
      errorToast('Please select at least one user');
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await usersApi.bulkUpdate(selectedUserIds, updates);
      if (response.success) {
        successToast(response.message || `Successfully updated ${response.count} user(s)`);
        setSelectedUserIds([]);
        fetchUsers();
      } else {
        throw new Error(response.message || 'Failed to update users');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedUserIds.length === 0) {
      errorToast('Please select at least one user');
      return;
    }

    const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
    const userNames = selectedUsers.map(u => u.name || u.username).join(', ');

    modals.openContextModal({
      modal: 'warningModal',
      title: 'Delete Selected Users',
      centered: true,
      innerProps: {
        message: `Are you sure you want to delete ${selectedUserIds.length} user(s)?\n\n${userNames}\n\nThis action cannot be undone.`,
        submit: async () => {
          try {
            setBulkActionLoading(true);
            const response = await usersApi.bulkDelete(selectedUserIds);
            if (response.success) {
              successToast(response.message || `Successfully deleted ${response.count} user(s)`);
              setSelectedUserIds([]);
              fetchUsers();
            } else {
              throw new Error(response.message || 'Failed to delete users');
            }
          } catch (error) {
            errorToast(error.message || 'Failed to delete users');
            throw error;
          } finally {
            setBulkActionLoading(false);
          }
        }
      }
    });
  };

  const columns = [
    {
      key: 'select',
      label: (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
            onChange={handleSelectAll}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
      sortable: false,
      render: (value, row) => (
        <input
          type="checkbox"
          checked={selectedUserIds.includes(row.id)}
          onChange={() => handleSelectUser(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
        />
      )
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
            {row.name.split(' ').map(n => n[0]).join('')}
          </div>
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true
    },
    {
      key: 'username',
      label: 'Username',
      sortable: true
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (value) => getRoleBadge(value)
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (value) => value || 'Never'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEditUser(row)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Edit user"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleResetPassword(row)}
            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
            title="Reset password"
          >
            <KeyIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteUser(row)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete user"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-8 mt-8">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-sm text-gray-500">Manage user accounts, roles, and permissions</p>
            </div>
          </div>
          <Button
            onClick={handleAddUser}
            leftSection={<PlusCircleIcon className="w-5 h-5" />}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Add User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-blue-500 p-6 hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                Total Users
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {loading ? '...' : users.length}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 p-6 hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                Active Users
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-green-600">
              {loading ? '...' : users.filter(u => u.status === 'active').length}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-gray-500 p-6 hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                Inactive Users
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <XCircleIcon className="w-6 h-6 text-gray-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-600">
              {loading ? '...' : users.filter(u => u.status === 'inactive').length}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-500 p-6 hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                Locked Accounts
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <LockClosedIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-red-600">
              {loading ? '...' : users.filter(u => u.status === 'locked').length}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TextInput
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />}
            className="flex-1"
          />
          <Select
            placeholder="Filter by role"
            value={roleFilter}
            onChange={setRoleFilter}
            data={[
              { value: 'all', label: 'All Roles' },
              { value: 'admin', label: 'Admin' },
              { value: 'bm', label: 'Branch Manager' },
              { value: 'sales', label: 'Sales' },
              { value: 'rep', label: 'Rep' },
              { value: 'fleet', label: 'Fleet' }
            ]}
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'locked', label: 'Locked' }
            ]}
          />
          <Select
            placeholder="Filter by branch"
            value={branchFilter}
            onChange={setBranchFilter}
            data={[
              { value: 'all', label: 'All Branches' },
              { value: 'AVDL', label: 'AVDL' },
              { value: 'AVDL2', label: 'AVDL2' },
              { value: 'Finchley', label: 'Finchley' }
            ]}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Users ({filteredUsers.length})
          </h2>
          {selectedUserIds.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleBulkUpdate({ status: 'active' })}
                  disabled={bulkActionLoading}
                  className="text-green-600 hover:text-green-700"
                >
                  Activate
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleBulkUpdate({ status: 'inactive' })}
                  disabled={bulkActionLoading}
                  className="text-gray-600 hover:text-gray-700"
                >
                  Deactivate
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleBulkUpdate({ allow_login: true })}
                  disabled={bulkActionLoading}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Enable Login
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleBulkUpdate({ allow_login: false })}
                  disabled={bulkActionLoading}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Disable Login
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setSelectedUserIds([])}
                  disabled={bulkActionLoading}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
        <DataTable
          data={filteredUsers}
          loading={loading}
          columns={columns}
          searchable={false}
          sortable={true}
          pagination={true}
          pageSize={10}
          emptyMessage="No users found"
        />
      </div>
    </div>
  );
};

export default UserManagement;


